import { useCallback, useMemo } from 'react'
import { getReportOriginSkill } from '@renderer/chat/integrations/report-to-user'
import { useIntegrationConnections } from '@renderer/hooks/use-integration-connections'
import { useForYouChatsFeed } from '@renderer/overlay/hooks/use-running-chats'
import type { SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { useVisibleStagedActions, type StagedAction } from '../../hooks/use-staged-actions'
import { useUpcomingViewModel } from '../../views/upcoming-view/use-upcoming-view-model'
import type { TourStop } from './tour-state'

/** Seed skill external id for Daily Brief. */
const DAILY_BRIEF_SKILL_ID = 'b0000000-0000-4000-8000-000000000105'

const isDailyBriefAction = (action: StagedAction): boolean => getReportOriginSkill(action) === 'Daily Brief'

/** A finished Daily Brief; a scheduled run has no skill id and a user's copy has its own, so title and report count. */
export const isDailyBriefChat = (chat: SidepanelChat, actionById: ReadonlyMap<string, StagedAction>): boolean =>
  !chat.isStreaming &&
  (chat.skillId === DAILY_BRIEF_SKILL_ID ||
    chat.title?.trim().toLowerCase() === 'daily brief' ||
    chat.stagedActionIds.some((id) => {
      const action = actionById.get(id)
      return action !== undefined && isDailyBriefAction(action)
    }))

/** Whether For You has a Daily Brief to walk to; the feed is newest first. */
function useHasDailyBrief(): boolean {
  const { data: chats } = useForYouChatsFeed()
  const { allActions } = useVisibleStagedActions()
  return useMemo(() => {
    const actionById = new Map(allActions.map((action) => [action.id, action]))
    return (chats ?? []).some((chat) => isDailyBriefChat(chat, actionById))
  }, [chats, allActions])
}

/** From the meeting rows Upcoming renders, so a filtered or recorded meeting is never picked: soonest with a brief, else today's latest. */
function useMeetingBriefToShow() {
  const { sections } = useUpcomingViewModel()
  return useMemo(() => {
    const upcoming = sections.flatMap((section) => (section.kind === 'events' ? section.events : []))
    const earlierToday = sections.flatMap((section) =>
      section.kind === 'earlier-today'
        ? section.items.flatMap((item) => (item.type === 'event' ? [item.event] : []))
        : [],
    )
    const hasBrief = (event: (typeof upcoming)[number]) => !!event.brief?.conversationId
    const byStart = (a: (typeof upcoming)[number], b: (typeof upcoming)[number]) =>
      new Date(a.start).getTime() - new Date(b.start).getTime()
    return upcoming.filter(hasBrief).sort(byStart)[0] ?? earlierToday.filter(hasBrief).sort(byStart).at(-1) ?? null
  }, [sections])
}

/** Fits a generated stop to what the app shows right now; null hides it until there is something to walk to. */
export function useLiveTourStop(): (stop: TourStop) => TourStop | null {
  const hasDailyBrief = useHasDailyBrief()
  const meetingBrief = useMeetingBriefToShow()
  const { active: connectedApps } = useIntegrationConnections()
  return useCallback(
    (stop: TourStop) => {
      switch (stop.mission) {
        case 'meeting-brief':
          return meetingBrief
            ? {
                ...stop,
                title: `Open your ${meetingBrief.title} brief`,
                params: { ...stop.params, meetingId: meetingBrief.id, meetingTitle: meetingBrief.title },
              }
            : null
        case 'daily-brief':
          return hasDailyBrief ? { ...stop, title: 'Read your Daily Brief' } : null
        case 'integrations':
          // An app connected since the tour was written leaves the stop pointing at no card.
          return connectedApps.some((app) => app.id === stop.params?.app)
            ? {
                mission: stop.mission,
                title: 'Connect another app',
                reason: 'Each app you add is one more place Highlight can search and act.',
              }
            : stop
        default:
          return stop
      }
    },
    [hasDailyBrief, meetingBrief, connectedApps],
  )
}
